import React from 'react';
import { translate } from '@utils/i18n';
import { formatFileSize } from '@features/Attachments/utils/fileFormatting';
import { QueueTextField } from '../components/QueueTextField';
import { ATTR_ANSWER, ATTR_CERTIFICATION_NUMBER, ATTR_INCLUDE_IN_COVER_LETTER, ATTR_SHOW_ON_REPORT, ATTR_TECHNICIAN_NAME, INSPECTION_ATTACHMENTS_NS, RESULT_FAIL, RESULT_NONE, RESULT_PASS, UPLOAD_NS } from '../constants';
/** Drawer column keys — the `fields` keys the queue rows read their values from. */
const COLUMN_TECHNICIAN = 'technician';
const COLUMN_CERTIFICATION_NUMBER = 'certificationNumber';
const COLUMN_RESULT = 'result';
/**
 * Module-level constant, not an inline literal: it feeds `useDirectUpload`'s `addFiles`
 * dependency, so a fresh object per render would rebuild that callback every time.
 *
 * `answer: null` is the "None" default — the Rails enum has no member for it, the column is simply
 * NULL, and the finalize endpoint normalises blank to NULL for the same reason.
 */
export const INSPECTION_DEFAULT_UPLOAD_ATTRIBUTES = {
    [ATTR_TECHNICIAN_NAME]: '',
    [ATTR_CERTIFICATION_NUMBER]: '',
    [ATTR_ANSWER]: null,
    [ATTR_SHOW_ON_REPORT]: false,
    [ATTR_INCLUDE_IN_COVER_LETTER]: false
};
/** Projects a staged row's finalize attributes onto the drawer columns' display values. */
export function toInspectionQueueFields(item) {
    var _a, _b, _c;
    return {
        [COLUMN_TECHNICIAN]: ((_a = item.attributes[ATTR_TECHNICIAN_NAME]) !== null && _a !== void 0 ? _a : ''),
        [COLUMN_CERTIFICATION_NUMBER]: ((_b = item.attributes[ATTR_CERTIFICATION_NUMBER]) !== null && _b !== void 0 ? _b : ''),
        // The select's own value — RESULT_NONE is a UI sentinel converted back to null on write.
        [COLUMN_RESULT]: ((_c = item.attributes[ATTR_ANSWER]) !== null && _c !== void 0 ? _c : RESULT_NONE)
    };
}
/** All `AddAttachmentsBrushaway` copy — the organism takes `labels` and never imports `translate`. */
export function buildAddAttachmentsLabels(maxSizeBytes) {
    const sizeLimitLabel = formatFileSize(maxSizeBytes);
    const NS = UPLOAD_NS;
    return {
        title: translate('title', { namespace: NS }),
        columnName: translate('column_name', { namespace: NS }),
        columnType: translate('column_type', { namespace: NS }),
        retryActionLabel: translate('retry_action', { namespace: NS }),
        removeActionLabel: (fileName) => translate('remove_action', { namespace: NS, fileName }),
        metaSize: (size) => translate('meta.size', { namespace: NS, size }),
        metaWaiting: (size) => translate('meta.waiting', { namespace: NS, size }),
        metaUploaded: (size) => translate('meta.uploaded', { namespace: NS, size }),
        metaFinalizing: (size) => translate('meta.finalizing', { namespace: NS, size }),
        footerTotals: (count, size) => translate('footer_totals', { namespace: NS, count, size }),
        cancelActionLabel: translate('cancel_action', { namespace: NS }),
        metaFailed: (size) => translate('meta.failed', { namespace: NS, size }),
        addButtonLabel: (count) => translate('add_button', { namespace: NS, count }),
        uploadingSummary: (done, total, pct) => translate('uploading_summary', { namespace: NS, done, total, pct }),
        uploadedSummary: (done, total, failed) => translate('uploaded_summary', { namespace: NS, done, total, failed }),
        cancelUploadLabel: translate('cancel_upload', { namespace: NS }),
        retryFailedLabel: translate('retry_failed', { namespace: NS }),
        rejectedAlertTitle: (count) => translate('rejected_alert.title', { namespace: NS, count }),
        rejectedAlertMessage: (fileName, size) => translate('rejected_alert.message', { namespace: NS, fileName, size, limit: sizeLimitLabel }),
        rejectedAlertTypeMessage: (fileName) => translate('rejected_alert.type_message', { namespace: NS, fileName }),
        dropzoneNote: translate('dropzone_note', { namespace: NS, limit: sizeLimitLabel }),
        removeConfirmTitle: translate('remove_confirm.title', { namespace: NS }),
        removeConfirmDescription: (fileName) => translate('remove_confirm.description', { namespace: NS, fileName }),
        removeConfirmConfirmLabel: translate('remove_confirm.confirm_label', { namespace: NS }),
        removeConfirmCancelLabel: translate('remove_confirm.cancel_label', { namespace: NS }),
        discardConfirmTitle: translate('discard_confirm.title', { namespace: NS }),
        discardConfirmDescription: translate('discard_confirm.description', { namespace: NS }),
        discardConfirmConfirmLabel: translate('discard_confirm.confirm_label', { namespace: NS }),
        discardConfirmCancelLabel: translate('discard_confirm.cancel_label', { namespace: NS }),
        closeLabel: translate('close_label', { namespace: NS })
    };
}
const tableT = (key) => translate(key, { namespace: INSPECTION_ATTACHMENTS_NS });
/**
 * Inspection's three drawer columns. Each `onChange` writes ONE finalize attribute, which is why
 * `updateItemMetadata` merges rather than replaces — setting a technician must not clear the
 * result the user already chose on that row.
 *
 * Report options are deliberately absent: the design's drawer carries File, Technician,
 * Certification # and Result only, and both report booleans default to false and are edited from
 * the table afterwards.
 */
export function buildAddAttachmentsColumns(updateItemMetadata) {
    const textColumn = (key, attribute, labelKey, placeholderKey, width) => ({
        kind: 'custom',
        key,
        label: translate(labelKey, { namespace: UPLOAD_NS }),
        width,
        renderer: ({ item, disabled }) => {
            var _a;
            return React.createElement(QueueTextField, {
                itemId: item.id,
                value: (_a = item.fields[key]) !== null && _a !== void 0 ? _a : '',
                disabled,
                placeholder: tableT(placeholderKey),
                // Blur, not change: a per-keystroke write re-renders the queue and the Name cell's
                // autofocus steals the caret back after the first letter.
                onCommit: (value) => updateItemMetadata(item.id, { attributes: { [attribute]: value } })
            });
        }
    });
    return [
        textColumn(COLUMN_TECHNICIAN, ATTR_TECHNICIAN_NAME, 'column_technician', 'placeholders.technician', '170px'),
        textColumn(COLUMN_CERTIFICATION_NUMBER, ATTR_CERTIFICATION_NUMBER, 'column_certification_number', 'placeholders.certification_number', '160px'),
        {
            kind: 'select',
            key: COLUMN_RESULT,
            label: translate('column_result', { namespace: UPLOAD_NS }),
            width: '130px',
            options: [
                { value: RESULT_NONE, label: tableT('result.none') },
                { value: RESULT_PASS, label: tableT('result.pass') },
                { value: RESULT_FAIL, label: tableT('result.fail') }
            ],
            // RESULT_NONE is a UI-only sentinel: the wire value must be null, or the Rails enum raises
            // ArgumentError at finalize — after the bytes are already in S3.
            onChange: (id, value) => updateItemMetadata(id, { attributes: { [ATTR_ANSWER]: value === RESULT_NONE ? null : value } })
        }
    ];
}
